import * as React from "react";
import { Subject, Subscription } from "rxjs";
import { WithStyles, withStyles, createStyles, Theme } from '../mui/styles';
import { Button, Grid } from "../widget";
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import { FileInfo as FormFileInfo } from "common/api/Forms";
import FileUpload from "../../ajax/FileUpload";
import UploadFilesList from "./UploadFilesList";
import { FileInfo } from "./FileInfo";


export interface UploadedFileInfo {
    fileId: number;
    name: string;
}

export interface UploadFilesDialogProps {
    show: boolean;
    title?: string;
    uploadFileUrl: string;
    onClose: (uploadedFiles?: Array<FormFileInfo>) => void;
    maxFiles: number;
    allowedExtensions: string[];
}

type PropsType = UploadFilesDialogProps & WithStyles<typeof styles>;

interface State {
    files: Array<FileInfo>;
    uploading: boolean;
}

class UploadFilesDialog extends React.PureComponent<PropsType, State> {
    constructor(props: PropsType) {
        super(props);
        this.state = {
            files: [],
            uploading: false,
        }

        this.inputFile = React.createRef<HTMLInputElement>();
    }

    inputFile: React.RefObject<HTMLInputElement>;
    canceled: boolean = false;

    hasAllowedExtension = (file: File) => {
        const { allowedExtensions } = this.props;

        const re = /(?:\.([^.]+))?$/;
        const ext = re.exec(file.name)[1].toLowerCase();
        return allowedExtensions.indexOf(ext) >= 0;
    }

    addFile = (file: File) => {
        const { allowedExtensions } = this.props;

        this.setState(state => {

            const fileInfo: FileInfo = {
                file: file,
                name: file.name,
                uploaded: false,
                uploading: false,
                hasError: false,
                progress: 0,
                upload: new Subject(),
            };

            if (this.hasAllowedExtension(file) === false) {
                fileInfo.hasError = true;
                fileInfo.errorMessage = 'Fomát souboru není žádný z povolených (' + allowedExtensions.join(', ') + '). Vyberte jiný soubor a přesvědčte se, že má správný formát.';
            }

            return { files: state.files.concat([fileInfo]) }
        });

    }

    removeFile = (f: FileInfo) => {
        this.setState(state => {
            const index = state.files.indexOf(f);
            const newFiles = state.files.concat();
            newFiles.splice(index, 1);
            return { files: newFiles }
        });
    }

    uploadedFiles: Subscription[] = [];

    uploadCoppleted = (index: number) => {

        this.uploadedFiles[index].unsubscribe();
        this.uploadedFiles[index] = undefined;

        this.checkUploadDone();
    }

    checkUploadDone = () => {
        const { files } = this.state;
        const pendings = this.uploadedFiles.filter((x) => x !== undefined);
        const uploaded = files.filter(x => x.uploaded === true);
        const completed = pendings.length === 0;
        const hasError = completed && uploaded.length < files.length;

        if (completed) {
            this.uploadedFiles = [];
            if (hasError === false && uploaded.length > 0) {
                this.props.onClose(this.canceled === false ? uploaded : undefined);
            }
            this.setState({ uploading: false });
        }

    }

    uploadFiles = () => {

        const { uploadFileUrl } = this.props;
        const { files } = this.state;

        this.setState({ uploading: true });

        const filesForUpload = files.filter(x => x.uploaded === false && x.hasError === false);

        for (let i = 0; i < filesForUpload.length; i++) {

            const fileInfo = filesForUpload[i];

            this.uploadedFiles.push(FileUpload(uploadFileUrl, fileInfo.file).subscribe(
                fi => {
                    fileInfo.upload.next(fi);

                    if (fi.state == 'done') {
                        fileInfo.fileId = fi.fileId;
                        fileInfo.uploaded = true;
                    }
                },
                err => {
                    console.error(err);
                },
                () => {
                    this.uploadCoppleted(i);
                })
            );
        }


        this.checkUploadDone();

    }

    cancel = () => {
        this.canceled = true;
        this.props.onClose();
    }

    handlenInputFile = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation();
        event.preventDefault();

        for (let i = 0; i < event.target.files.length; i++) {
            this.addFile(event.target.files[i]);
        }
    }

    openFileFialog = () => {
        this.inputFile.current.value = '';
        this.inputFile.current.click();
    }

    onEnter = () => {
        this.canceled = false;
        this.setState({
            files: [],
            uploading: false,
        });
    }

    render() {

        const { show, title, classes, maxFiles, allowedExtensions } = this.props;
        const { files, uploading } = this.state;

        const disableUpload = files.length == 0 || uploading;

        return <Dialog open={show} fullWidth maxWidth="md" onEnter={this.onEnter}>
            <DialogTitle disableTypography className={classes.titleRoot}>
                <Typography variant="h6">{title || 'Vložení souborů'}</Typography>
                <IconButton aria-label="Close" className={classes.titleCloseButton} onClick={this.cancel}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            {files.length > 0
                ? <DialogContent className={classes.contentRoot}>
                    <UploadFilesList files={files} removeFile={this.removeFile} uploading={uploading} allowedExtensions={allowedExtensions} />
                    {files.length < maxFiles ? <Button color="primary" variant="outlined" onClick={this.openFileFialog} disabled={uploading}>{'Přidat soubor (' + allowedExtensions.join(', ') + ')'}</Button> : null}
                </DialogContent>
                : <DialogContent className={classes.contentRoot}>
                    {/*
                    <div>{'Drag files here'}</div>
                    <div>{'— or —'}</div>
                    */}
                    <div>
                        <Button color="primary" variant="outlined" onClick={this.openFileFialog} disabled={uploading}>{'Vyberte soubor (' + allowedExtensions.join(', ') + ') z vašeho zařízení'}</Button>
                    </div>
                </DialogContent>}
            <input hidden type='file' id='file' ref={this.inputFile} onChange={this.handlenInputFile} accept={allowedExtensions.map(x => '.' + x).join(', ')} multiple={maxFiles > 1} />
            <DialogActions className={classes.actionsRoot}>
                <Grid container direction="row" spacing={8}>
                    <Grid item>
                        <Button color="primary" variant="contained" onClick={this.uploadFiles} disabled={disableUpload}>{'Odeslat'}</Button>
                    </Grid>
                    <Grid item>
                        <Button variant="contained" onClick={this.cancel}>{'Zavřít'}</Button>
                    </Grid>
                </Grid>
            </DialogActions>
        </Dialog>;
    }
}


const styles = (theme: Theme) => createStyles({
    titleRoot: {
        borderBottom: `2px solid ${theme.palette.divider}`,
        margin: 0,
        padding: theme.spacing.unit * 2,
    },
    titleCloseButton: {
        position: 'absolute',
        right: theme.spacing.unit,
        top: theme.spacing.unit,
        color: theme.palette.grey[500],
    },
    contentRoot: {
        margin: 0,
        padding: theme.spacing.unit * 2,
    },
    actionsRoot: {
        borderTop: `1px solid ${theme.palette.divider}`,
        margin: 0,
        padding: theme.spacing.unit,
    }
});

export default withStyles(styles)(UploadFilesDialog);
